import React, { useState, useEffect, useRef, useMemo } from "react"
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts"
import { CalendarDaysIcon, CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import DataTable from "aldoo-ra/DataTable"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { UsagesCRUD, GetUsagesStats } from "./api"

// ── Pricing (per 1M tokens, USD) ─────────────────────────────────────────────
const MODEL_PRICING = {
  "gemini-2.5-flash":          { input: 0.15,  output: 0.60  },
  "gemini-2.5-pro":            { input: 1.25,  output: 10.00 },
  "gemini-2.0-flash":          { input: 0.10,  output: 0.40  },
  "gemini-1.5-flash":          { input: 0.075, output: 0.30  },
  "gemini-1.5-pro":            { input: 1.25,  output: 5.00  },
  "gpt-4o":                    { input: 2.50,  output: 10.00 },
  "gpt-4o-mini":               { input: 0.15,  output: 0.60  },
  "gpt-4-turbo":               { input: 10.00, output: 30.00 },
  "claude-opus-4-6":           { input: 15.00, output: 75.00 },
  "claude-sonnet-4-6":         { input: 3.00,  output: 15.00 },
  "claude-haiku-4-5-20251001": { input: 0.80,  output: 4.00  },
}

function calcCost(inputTokens, outputTokens, modelId) {
  const pricing = MODEL_PRICING[modelId]
  if (!pricing) return null
  return (inputTokens / 1e6) * pricing.input + (outputTokens / 1e6) * pricing.output
}

// ── Date range helpers ────────────────────────────────────────────────────────
const DATE_RANGES = [
  { label: "Last 24 Hours", hours: 24 },
  { label: "Last 7 Days",   days: 7  },
  { label: "Last 30 Days",  days: 30 },
  { label: "Last 90 Days",  days: 90 },
  { label: "Last 365 Days", days: 365 },
  { label: "Lifetime" },
  { label: "Custom", custom: true },
]

function getDateRange(rangeOpt, customRange) {
  if (rangeOpt.custom) {
    if (!customRange) return { dateFrom: null, dateTo: null }
    const start = customRange.startDate instanceof Date ? customRange.startDate : new Date(customRange.startDate)
    const end   = customRange.endDate   instanceof Date ? customRange.endDate   : new Date(customRange.endDate)
    end.setHours(23, 59, 59, 999)
    return { dateFrom: start.toISOString(), dateTo: end.toISOString() }
  }
  if (rangeOpt.hours) return { dateFrom: new Date(Date.now() - rangeOpt.hours * 3600000).toISOString(), dateTo: null }
  if (rangeOpt.days)  return { dateFrom: new Date(Date.now() - rangeOpt.days  * 86400000).toISOString(), dateTo: null }
  return { dateFrom: null, dateTo: null }
}

function fmtDate(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function rangeTriggerLabel(rangeOpt, dateFrom, customRange) {
  if (!rangeOpt) return ""
  if (!rangeOpt.days && !rangeOpt.hours && !rangeOpt.custom) return "Lifetime"
  if (rangeOpt.custom) {
    if (!customRange) return "Custom Range"
    const s = customRange.startDate instanceof Date ? customRange.startDate : new Date(customRange.startDate)
    const e = customRange.endDate   instanceof Date ? customRange.endDate   : new Date(customRange.endDate)
    return `${fmtDate(s)} – ${fmtDate(e)}`
  }
  if (!dateFrom) return rangeOpt.label
  return `${rangeOpt.label}: ${fmtDate(new Date(dateFrom))} – ${fmtDate(new Date())}`
}

// ── Date Range Picker (admin-themed) ─────────────────────────────────────────
function DateRangePicker({ dateRangeIdx, customRange, onRangeSelect, onCustomRange, dateFrom }) {
  const [open, setOpen]             = useState(false)
  const [dropdownStyle, setStyle]   = useState({})
  const [customStart, setStart]     = useState("")
  const [customEnd,   setEnd]       = useState("")
  const buttonRef = useRef(null)
  const panelRef  = useRef(null)

  useEffect(() => {
    if (!customRange) return
    const s = customRange.startDate instanceof Date ? customRange.startDate : new Date(customRange.startDate)
    const e = customRange.endDate   instanceof Date ? customRange.endDate   : new Date(customRange.endDate)
    setStart(s.toISOString().slice(0, 10))
    setEnd(e.toISOString().slice(0, 10))
  }, [customRange])

  const calcPos = () => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const w = 256
    let left = rect.left + rect.width / 2 - w / 2
    if (left < 8) left = 8
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8
    setStyle({ position: "fixed", top: rect.bottom + 8, left, width: w, zIndex: 9999 })
  }

  useEffect(() => {
    if (!open) return
    const close = (e) => {
      if (buttonRef.current?.contains(e.target)) return
      if (panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener("mousedown", close)
    window.addEventListener("resize", calcPos)
    return () => { document.removeEventListener("mousedown", close); window.removeEventListener("resize", calcPos) }
  }, [open])

  const handleCustomChange = (start, end) => {
    if (!start || !end) return
    const customIdx = DATE_RANGES.findIndex((r) => r.custom)
    onRangeSelect(customIdx)
    onCustomRange({ startDate: new Date(start), endDate: new Date(end) })
  }

  const rangeOpt = DATE_RANGES[dateRangeIdx]
  const label = rangeTriggerLabel(rangeOpt, dateFrom, customRange)
  const customIdx = DATE_RANGES.findIndex((r) => r.custom)

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => { if (!open) calcPos(); setOpen((v) => !v) }}
        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-admin_bordered text-admin_text hover:border-admin_primary/40 hover:bg-admin_primary/5 transition"
      >
        <CalendarDaysIcon className="w-3.5 h-3.5 shrink-0 text-admin_text_secondary" />
        {label}
        <ChevronDownIcon className={`w-3 h-3 shrink-0 text-admin_text_secondary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div ref={panelRef} style={dropdownStyle} className="bg-admin_paper rounded-xl shadow-xl border border-admin_bordered py-2">
          {DATE_RANGES.filter((r) => !r.custom).map((r, i) => (
            <button key={r.label} onClick={() => { onRangeSelect(i); setOpen(false) }}
              className="w-full flex items-center gap-3 px-4 py-2 text-xs hover:bg-admin_neutral_1 text-admin_text transition">
              {dateRangeIdx === i
                ? <CheckIcon className="w-3.5 h-3.5 text-admin_primary shrink-0" />
                : <span className="w-3.5 h-3.5 shrink-0" />}
              {r.label}
            </button>
          ))}
          <div className="border-t border-admin_bordered mt-2 pt-3 px-4 pb-1">
            <p className="text-[10px] text-admin_text_secondary mb-2 uppercase tracking-wide">Custom Range</p>
            <div className="flex gap-2">
              <input type="date" value={customStart}
                onClick={() => onRangeSelect(customIdx)}
                onChange={(e) => { setStart(e.target.value); handleCustomChange(e.target.value, customEnd) }}
                className="flex-1 text-xs border border-admin_bordered rounded-lg px-2 py-1.5 text-admin_text bg-admin_canvas cursor-pointer" />
              <input type="date" value={customEnd}
                onClick={() => onRangeSelect(customIdx)}
                onChange={(e) => { setEnd(e.target.value); handleCustomChange(customStart, e.target.value) }}
                className="flex-1 text-xs border border-admin_bordered rounded-lg px-2 py-1.5 text-admin_text bg-admin_canvas cursor-pointer" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Chart type toggle ─────────────────────────────────────────────────────────
const CHART_TYPES = [
  { value: "area", label: "Area" },
  { value: "bar",  label: "Bar"  },
  { value: "line", label: "Line" },
]

function ChartTypeToggle({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {CHART_TYPES.map((t) => (
        <button key={t.value} onClick={() => onChange(t.value)}
          className={`text-xs px-2.5 py-1 rounded-lg border transition ${
            value === t.value
              ? "border-admin_primary/40 bg-admin_primary/10 text-admin_primary font-medium"
              : "border-admin_bordered text-admin_text_secondary hover:border-admin_primary/30 hover:text-admin_text"
          }`}>
          {t.label}
        </button>
      ))}
    </div>
  )
}

// ── Custom tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-admin_paper border border-admin_bordered rounded-xl shadow-lg px-3 py-2.5 text-xs">
      {label && <div className="font-semibold text-admin_text_secondary mb-2 border-b border-admin_bordered pb-1.5">{label}</div>}
      <div className="flex flex-col gap-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color || p.stroke || p.fill }} />
            <span className="text-admin_text_secondary">{p.name}</span>
            <span className="text-admin_text font-semibold ml-auto pl-3">{p.value?.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Cost card ─────────────────────────────────────────────────────────────────
function CostCard({ modelId, inputTokens, outputTokens }) {
  const pricing = MODEL_PRICING[modelId]
  const cost    = calcCost(inputTokens, outputTokens, modelId)

  return (
    <div className="bg-admin_paper border border-admin_bordered rounded-xl p-4">
      <p className="text-xs font-mono text-admin_text_secondary mb-2 truncate">{modelId}</p>
      <p className="text-2xl font-bold text-admin_text mb-1">
        {cost != null ? `$${cost.toFixed(4)}` : <span className="text-admin_text_secondary text-base">—</span>}
      </p>
      {pricing && (
        <p className="text-[10px] text-admin_text_secondary">
          ${pricing.input}/M in · ${pricing.output}/M out
        </p>
      )}
      {!pricing && (
        <p className="text-[10px] text-admin_warning">Pricing not configured</p>
      )}
      <div className="mt-2 pt-2 border-t border-admin_bordered grid grid-cols-2 gap-1 text-[10px] text-admin_text_secondary">
        <span>In: {(inputTokens || 0).toLocaleString()}</span>
        <span>Out: {(outputTokens || 0).toLocaleString()}</span>
      </div>
    </div>
  )
}

const SERIES = [
  { key: "inputTokens",  name: "Input Tokens",  color: "#6366f1" },
  { key: "outputTokens", name: "Output Tokens", color: "#10b981" },
  { key: "requestCount", name: "Requests",       color: "#f59e0b" },
]

function formatNum(n) {
  if (!n && n !== 0) return "—"
  return n.toLocaleString()
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function UsageBrowser() {
  const [data,         setData]         = useState(null)
  const [refreshKey,   setRefreshKey]   = useState(0)
  const [chartType,    setChartType]    = useState("area")
  const [dateRangeIdx, setDateRangeIdx] = useState(1)  // Last 7 Days default
  const [customRange,  setCustomRange]  = useState(null)
  const [filterModel,  setFilterModel]  = useState(null)
  const [filterAgent,  setFilterAgent]  = useState(null)
  const [hiddenSeries, setHiddenSeries] = useState(new Set())

  const { dateFrom, dateTo } = useMemo(
    () => getDateRange(DATE_RANGES[dateRangeIdx], customRange),
    [dateRangeIdx, customRange]
  )

  useEffect(() => {
    const params = {}
    if (dateFrom)     params.dateFrom = dateFrom
    if (dateTo)       params.dateTo   = dateTo
    if (filterModel)  params.modelId  = filterModel
    if (filterAgent)  params.agentId  = filterAgent
    GetUsagesStats(params).then((res) => {
      if (res.result) setData(res)
    })
  }, [dateFrom, dateTo, filterModel, filterAgent, refreshKey])

  const summary      = data?.summary      || { inputTokens: 0, outputTokens: 0, requestCount: 0 }
  const timeSeries   = data?.timeSeries   || []
  const modelBreakdown = data?.modelBreakdown || []
  const modelOptions = (data?.models || []).map((m) => ({ value: m, label: m }))
  const agentOptions = (data?.agents || []).map((a) => ({ value: a, label: a }))

  const totalCost = modelBreakdown.reduce((acc, m) => {
    const c = calcCost(m.inputTokens, m.outputTokens, m.modelId)
    return acc + (c || 0)
  }, 0)

  const toggleSeries = (e) => {
    const key = e.dataKey
    setHiddenSeries((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const renderChart = (series) => {
    const common = {
      data: timeSeries,
      margin: { top: 4, right: 16, left: 0, bottom: 0 }
    }
    const axis = (
      <>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" />
        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => {
          const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`
        }} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
        <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 9999, outline: "none" }} />
        <Legend wrapperStyle={{ fontSize: 11, cursor: "pointer" }} onClick={toggleSeries}
          formatter={(value, entry) => (
            <span style={{ opacity: hiddenSeries.has(entry.dataKey) ? 0.4 : 1 }}>{value}</span>
          )} />
      </>
    )

    if (chartType === "bar") return (
      <BarChart {...common}>
        {axis}
        {series.map((s) => <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[3,3,0,0]} hide={hiddenSeries.has(s.key)} />)}
      </BarChart>
    )
    if (chartType === "line") return (
      <LineChart {...common}>
        {axis}
        {series.map((s) => <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} dot={false} strokeWidth={2} hide={hiddenSeries.has(s.key)} />)}
      </LineChart>
    )
    return (
      <AreaChart {...common}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`grad_${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={s.color} stopOpacity={0.25} />
              <stop offset="95%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        {axis}
        {series.map((s) => (
          <Area key={s.key} type="monotone" dataKey={s.key} name={s.name}
            stroke={s.color} fill={`url(#grad_${s.key})`} strokeWidth={2} dot={false} hide={hiddenSeries.has(s.key)} />
        ))}
      </AreaChart>
    )
  }

  const tableColumns = [
    {
      id: "date",
      header: "Date",
      headerClassName: "w-[18%]",
      sortable: true,
      render: (v) => <span className="text-xs text-admin_text_secondary">{v ? new Date(v).toLocaleDateString() : "—"}</span>
    },
    {
      id: "modelId",
      header: "Model",
      headerClassName: "w-[25%]",
      render: (v) => <span className="text-xs bg-admin_neutral_1 px-2 py-0.5 rounded">{v}</span>
    },
    {
      id: "agentId",
      header: "Agent",
      headerClassName: "w-[20%]",
      render: (v) => <span className="text-xs text-admin_text">{v || "—"}</span>
    },
    {
      id: "inputTokens",
      header: "Input Tokens",
      headerClassName: "w-[13%]",
      sortable: true,
      render: (v) => <span className="text-xs text-admin_text">{formatNum(v)}</span>
    },
    {
      id: "outputTokens",
      header: "Output Tokens",
      headerClassName: "w-[13%]",
      sortable: true,
      render: (v) => <span className="text-xs text-admin_text">{formatNum(v)}</span>
    },
    {
      id: "requestCount",
      header: "Requests",
      headerClassName: "w-[11%]",
      sortable: true,
      render: (v) => <span className="text-xs text-admin_text">{formatNum(v)}</span>
    }
  ]

  const tableRequest = useMemo(() => {
    const match = {}
    if (filterModel) match.modelId = filterModel
    if (filterAgent) match.agentId = filterAgent
    if (dateFrom || dateTo) {
      match.date = {}
      if (dateFrom) match.date.$gte = dateFrom
      if (dateTo)   match.date.$lte = dateTo
    }
    return { match }
  }, [filterModel, filterAgent, dateFrom, dateTo])

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Typography variant="h2">Usage</Typography>
        <div className="flex items-center gap-2">
          {modelOptions.length > 0 && (
            <DropDownSelect
              variant="admin"
              className="w-44"
              placeholder="All Models"
              options={modelOptions}
              value={filterModel ? { value: filterModel } : null}
              onChange={(sel) => setFilterModel((Array.isArray(sel) ? sel[0] : sel)?.value || null)}
              allowEmptySelection
            />
          )}
          {agentOptions.length > 0 && (
            <DropDownSelect
              variant="admin"
              className="w-44"
              placeholder="All Agents"
              options={agentOptions}
              value={filterAgent ? { value: filterAgent } : null}
              onChange={(sel) => setFilterAgent((Array.isArray(sel) ? sel[0] : sel)?.value || null)}
              allowEmptySelection
            />
          )}
          <DateRangePicker
            dateRangeIdx={dateRangeIdx}
            customRange={customRange}
            dateFrom={dateFrom}
            onRangeSelect={setDateRangeIdx}
            onCustomRange={setCustomRange}
          />
          <Button variant="secondary" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>Refresh</Button>
        </div>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-4">
          <p className="text-xs text-admin_text_secondary mb-1">Total Requests</p>
          <p className="text-2xl font-bold text-admin_text">{formatNum(summary.requestCount)}</p>
        </div>
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-4">
          <p className="text-xs text-admin_text_secondary mb-1">Input Tokens</p>
          <p className="text-2xl font-bold text-admin_text">{formatNum(summary.inputTokens)}</p>
        </div>
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-4">
          <p className="text-xs text-admin_text_secondary mb-1">Output Tokens</p>
          <p className="text-2xl font-bold text-admin_text">{formatNum(summary.outputTokens)}</p>
        </div>
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-4">
          <p className="text-xs text-admin_text_secondary mb-1">Est. Total Cost</p>
          <p className="text-2xl font-bold text-admin_text">${totalCost.toFixed(4)}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-admin_paper border border-admin_bordered rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-admin_text">Token & Request Volume</p>
          <ChartTypeToggle value={chartType} onChange={setChartType} />
        </div>
        {timeSeries.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-sm text-admin_text_secondary">No data for selected period</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            {renderChart(SERIES)}
          </ResponsiveContainer>
        )}
      </div>

      {/* Cost breakdown per model */}
      {modelBreakdown.length > 0 && (
        <div>
          <Typography variant="h4" className="mb-3">Cost by Model</Typography>
          <div className="grid grid-cols-3 gap-4">
            {modelBreakdown.map((m) => (
              <CostCard key={m.modelId} modelId={m.modelId} inputTokens={m.inputTokens} outputTokens={m.outputTokens} />
            ))}
          </div>
        </div>
      )}

      {/* Data table */}
      <DataTable
        refreshKey={refreshKey}
        renders={tableColumns}
        apiMethod={UsagesCRUD.Get}
        requestFields={tableRequest}
      />
    </div>
  )
}
